<template>
  <div
    class="web-slider"
    :class="{
      'web-slider--full': normalizedMode === 'full',
      'web-slider--container': normalizedMode === 'container',
    }"
    :style="sliderOuterStyle"
    @click="carouselClick"
  >
    <div class="web-slider__inner" :style="sliderInnerStyle">
      <b-carousel
        id="carousel-1"
        :interval="5000"
        :indicators="sliders.length > 1"
        :controls="sliders.length > 1"
        img-width="1024"
        img-height="480"
        class="web-slider__carousel"
      >
        <b-carousel-slide
          v-for="(slider, i) in sliders"
          :key="`slider-${i}`"
          :img-src="hasImage(slider) ? slider.src : undefined"
          :img-blank="!hasImage(slider)"
          :img-blank-color="getSlideBackgroundColor(slider)"
          class="click web-slider__slide"
          :class="{ 'web-slider__slide--text-only': !hasImage(slider) }"
        >
          <div
            class="web-slider__content"
            :class="{ 'web-slider__content--text-only': !hasImage(slider) }"
            :style="{
              color: getSlideTextColor(slider),
              textShadow: hasImage(slider) ? '1px 1px 2px #333' : 'none',
            }"
          >
            <h2
              v-if="slider.caption"
              class="web-slider__title"
              :style="parseInlineCss(slider.titleCss)"
            >
              {{ slider.caption }}
            </h2>
            <p
              v-if="slider.text"
              class="web-slider__text"
              :style="parseInlineCss(slider.textCss)"
            >
              {{ slider.text }}
            </p>
            <router-link
              class="btn px-5"
              :class="hasImage(slider) ? 'btn-outline-light' : 'btn-light'"
              :style="parseInlineCss(slider.buttonCss)"
              v-if="slider.to"
              :to="getLink(slider.to)"
            >
              {{ slider.buttonLabel || "VER" }}
            </router-link>
          </div>
        </b-carousel-slide>
      </b-carousel>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    sliders: {
      type: Array,
      default: () => [],
    },
    mode: {
      type: String,
      default: "container",
    },
    padding: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    normalizedMode() {
      return this.mode === "full" ? "full" : "container";
    },
    normalizedPadding() {
      return Math.max(0, Number(this.padding || 0));
    },
    sliderOuterStyle() {
      if (this.normalizedMode === "full") {
        return {
          paddingLeft: `${this.normalizedPadding}px`,
          paddingRight: `${this.normalizedPadding}px`,
        };
      }

      return {
        paddingLeft: `${this.normalizedPadding}px`,
        paddingRight: `${this.normalizedPadding}px`,
      };
    },
    sliderInnerStyle() {
      if (this.normalizedMode === "container") {
        return {
          maxWidth: "1300px",
          margin: "0 auto",
        };
      }

      return {};
    },
  },
  methods: {
    hasImage(slider = {}) {
      return Boolean(String(slider.src || "").trim());
    },
    getSlideBackgroundColor(slider = {}) {
      const color = String(slider.bgColor || "").trim();
      if (!color || color.toLowerCase() === "transparent") return "#ffffff";
      return color;
    },
    getSlideTextColor(slider = {}) {
      if (this.hasImage(slider)) return "#ffffff";
      const color = this.getSlideBackgroundColor(slider).replace("#", "");
      if (!/^[0-9a-f]{6}$/i.test(color)) return "#20313f";
      const r = parseInt(color.slice(0, 2), 16);
      const g = parseInt(color.slice(2, 4), 16);
      const b = parseInt(color.slice(4, 6), 16);
      const luminance = (r * 299 + g * 587 + b * 114) / 1000;
      return luminance > 160 ? "#20313f" : "#ffffff";
    },
    parseInlineCss(value = "") {
      return String(value || "")
        .split(";")
        .map((entry) => entry.trim())
        .filter(Boolean)
        .reduce((styles, rule) => {
          const separatorIndex = rule.indexOf(":");
          if (separatorIndex === -1) return styles;

          const property = rule.slice(0, separatorIndex).trim();
          const cssValue = rule.slice(separatorIndex + 1).trim();
          if (!property || !cssValue) return styles;

          const camelProperty = property.replace(/-([a-z])/g, (_, letter) =>
            letter.toUpperCase()
          );
          styles[camelProperty] = cssValue;
          return styles;
        }, {});
    },
    carouselClick() {},
  },
};
</script>

<style scoped>
.web-slider {
  width: 100%;
  margin-bottom: 1rem;
}

.web-slider--full {
  position: relative;
  width: 100vw;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
}

.web-slider__inner {
  width: 100%;
}

.web-slider__carousel {
  background: transparent;
}

.web-slider__slide--text-only {
  background: transparent;
}

.web-slider__content {
  min-height: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem 1.5rem;
}

.web-slider__content--text-only {
  min-height: 320px;
}

.web-slider__title {
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 800;
  margin-bottom: 0.75rem;
}

.web-slider__text {
  font-size: clamp(1rem, 1.8vw, 1.35rem);
  margin-bottom: 1.25rem;
  max-width: 720px;
}
</style>
